import type { CookieHelpers } from './cookie';
import { isMobile } from './isMobile';

export type AppClientConfig = {
	clientId: string;
	apiKey: string;
	deviceId?: string;
	uuid?: string;
	appVersion?: string;
	at?: string;
};

export function getCommonHeaders(config: AppClientConfig) {
	// The Authorization header is owned ENTIRELY by the login SDK — `auth.interceptor`
	// sets `Bearer <at>` on both client (browserAuth, client.tsx) and server
	// (request-scoped serverAuth, server.ts). This host helper never reads `at` as
	// a credential; it only uses its presence to decide whether to send `uuid`
	// (anonymous-only device identifier).
	const { deviceId, uuid, appVersion, at, clientId, apiKey } = config;
	const headers: Record<string, string> = {
		apiKey,
		clientId,
		ixiSrc: clientId,
	};

	if (deviceId) {
		headers.deviceId = deviceId;
	}
	if (appVersion) {
		headers.appVersion = appVersion;
	}
	// uuid is the logged-out device identifier — only sent when there's no session.
	if (!at) {
		if (uuid) {
			headers.uuid = uuid;
		} else if (deviceId) {
			headers.uuid = deviceId;
		}
	}
	return headers;
}

/**
 * Clients whose API key breaks the `${clientId}!2$` convention. Mirrors
 * ixigo-pay's `getApiKey` — ixitrio (trains iOS) has no `!`, and deriving the
 * conventional key for it makes the gateway reject the request. Only consulted
 * when neither the launch URL nor the `apiKey` cookie supplied a key.
 */
const API_KEY_OVERRIDES: Record<string, string> = {
	ixitrio: 'ixitrio2$',
};

function apiKeyFor(clientId: string) {
	return API_KEY_OVERRIDES[clientId] || `${clientId}!2$`;
}

/**
 * Resolve the surface's `clientId`/`apiKey` (plus device metadata) for a request.
 *
 * The API key is taken, in order, from:
 *   1. `preferredApiKey` — the `?apiKey=` param the PWA launch URL carries;
 *   2. the `apiKey` cookie, but only when it belongs to the client we resolved
 *      (a launch URL naming a different client must not reuse the stored key);
 *   3. {@link apiKeyFor} — the per-client convention, with overrides.
 */
export function getRequestContext({
	clientId: preferredClientId,
	apiKey: preferredApiKey,
	getCookie,
	ua,
}: {
	clientId?: string;
	apiKey?: string;
	getCookie: (key: string) => string | undefined;
	ua: string;
}): AppClientConfig {
	const CONFIG = {
		apiKey: 'ixiweb!2$',
		clientId: 'ixiweb',
	};

	const CONFIG_mobile = {
		apiKey: 'iximweb!2$',
		clientId: 'iximweb',
	};

	const allCookies = [
		'at',
		'clientId',
		'apiKey',
		'uuid',
		'appVersion',
		'ixiUID',
	].reduce(
		(acc, key) => {
			acc[key] = getCookie(key);
			return acc;
		},
		{} as Record<string, string | undefined>,
	);

	const {
		ixiUID: deviceId,
		clientId,
		apiKey,
		uuid,
		appVersion,
		at,
	} = allCookies as Record<string, string>;

	const isMobileDevice = isMobile(ua);

	const clientConfig = isMobileDevice
		? {
				clientId: CONFIG_mobile.clientId,
				apiKey: CONFIG_mobile.apiKey,
			}
		: CONFIG;

	const resolvedClientId = preferredClientId || clientId;
	// The stored key belongs to the cookie's client — reuse it only when that's
	// the client we ended up with.
	const cookieApiKey = resolvedClientId === clientId ? apiKey : undefined;

	const client = resolvedClientId
		? {
				clientId: resolvedClientId,
				apiKey: preferredApiKey || cookieApiKey || apiKeyFor(resolvedClientId),
			}
		: clientConfig;

	return {
		deviceId,
		...client,
		uuid,
		appVersion,
		at,
	};
}

/**
 * @param cookieStore - cookie store to use for storing the ixiUID
 * @returns a function that returns the ixiUID
 */
export function ixiUIDResolver(cookieStore: CookieHelpers) {
	let ixiUIDPromise: Promise<string> | undefined;
	return async () => {
		const ixiUID = cookieStore.getCookie('ixiUID');
		if (ixiUID) return ixiUID;
		if (!ixiUIDPromise) {
			ixiUIDPromise = fetch(
				'https://edge-dyn.ixigo.com/eJwzdTE1cXEyNTOwMDExMXB0dnR0daxx/userSource.js',
				{ credentials: 'include' },
			)
				.then((res) => {
					return res.text();
				})
				.catch(() => {
					// Check if running on localhost
					const isLocalhost =
						typeof window !== 'undefined' &&
						(window.location.hostname === 'localhost' ||
							window.location.hostname === '127.0.0.1');
					if (!isLocalhost) {
						return '';
					}
					const randomValue = Array.from(
						crypto.getRandomValues(new Uint8Array(15)),
					)
						.map((byte) => byte.toString(36))
						.join('')
						.slice(0, 20);
					cookieStore.setCookie('ixiUID', randomValue);
					return randomValue;
				})
				.finally(() => {
					cookieStore.syncCookies();
				});
		}
		await ixiUIDPromise;
		return cookieStore.getCookie('ixiUID');
	};
}

export interface AppContext {
	clientConfig: () => AppClientConfig;
	cookie: CookieHelpers;

	/**
	 * Computed on the server, recovered on the client, and updated during a
	 * native handoff when the bridge supplies a different client identity.
	 * this should not be part of AppContext. we'll figure out a way to avoid this.
	 *
	 * this has been done to avoid leaking of javascript from theme module & pwa which calculates theme for each client
	 */
	_theme?: {
		/**
		 * Precomputed CSS for theming. Injected on the server and reused on the client during hydration.
		 */
		style?: string;

		/**
		 * script to detect theme preference and set the theme initially to avoid paint flashing
		 */
		script?: string;
	};
}
